import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
    CardDescription,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { User, Shield, Key, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { authApi } from "../../api";
import { translateBackendError } from "../../utils/errors";

export default function ForgotPin() {
    const { t, i18n } = useTranslation();
    const [identifier, setIdentifier] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [maskedEmail, setMaskedEmail] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [formErrors, setFormErrors] = useState<any>({});

    const validateForm = () => {
        const newErrors: any = {};
        if (!identifier) {
            newErrors.identifier = t('auth:signup.validation.emailRequired'); // Or identifierRequired
        } else if (identifier.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
            newErrors.identifier = t('auth:signup.validation.emailInvalid');
        }
        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await authApi.forgotPin({ identifier });
            if (response && (response as any).emailDestination) {
                setMaskedEmail((response as any).emailDestination);
            }
            setIsSuccess(true);
        } catch (err: any) {
            const rawMsg = err.response?.data?.message || err.message || t('common:errors.unknown');
            setError(translateBackendError(rawMsg, i18n.language));
        } finally {
            setIsLoading(false);
        }
    };

    // Success Screen
    if (isSuccess) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4">
                <Card className="w-full max-w-[400px] shadow-2xl bg-white/80 backdrop-blur-md border-white/50">
                    <CardHeader className="flex flex-col items-center text-center space-y-4">
                        <div className="bg-green-100 p-4 rounded-full">
                            <CheckCircle2 className="w-12 h-12 text-green-600" />
                        </div>
                        <CardTitle className="text-2xl font-bold">{t('auth:deviceVerification.emailSent')}</CardTitle>
                        <CardDescription className="text-center text-gray-600">
                            {t('auth:deviceVerification.approvalMessage')} <strong>{maskedEmail || identifier}</strong>.
                            {t('auth:deviceVerification.checkInbox')}
                        </CardDescription>
                    </CardHeader>
                    <CardFooter className="flex flex-col gap-3 pt-4">
                        <a href="/login" className="w-full">
                            <Button className="w-full">{t('auth:login.loginButton')}</Button>
                        </a>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4">
            <Card className="w-full max-w-[400px] shadow-2xl bg-white/80 backdrop-blur-md border-white/50 overflow-hidden relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
                <div className="absolute -top-24 -left-24 w-48 h-48 bg-primary/20 blur-3xl rounded-full pointer-events-none" />
                <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-accent/20 blur-3xl rounded-full pointer-events-none" />

                <CardHeader className="flex flex-col items-center text-center space-y-4">
                    <div className="bg-primary-50 p-3 rounded-full mb-2">
                        <a href="https://bousanm.com">
                            <img src="/logo.webp" alt="Logo" className="w-16 h-16 object-contain" />
                        </a>
                    </div>
                    <CardTitle className="text-2xl font-bold">{t('auth:login.forgotPin')}</CardTitle>

                    {/* Stepper */}
                    <div className="w-full max-w-sm mt-6">
                        <div className="flex items-start justify-between">
                            {/* Step 1 - Active */}
                            <div className="flex flex-col items-center">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-semibold mb-2">
                                    <User size={16} className="sm:w-5 sm:h-5" />
                                </div>
                                <span className="text-[10px] sm:text-xs font-medium text-primary-600 text-center">{t('auth:signup.steps.info')}</span>
                            </div>

                            {/* Connector 1 */}
                            <Separator className="flex-1 mx-2 sm:mx-4 mt-4 sm:mt-5 bg-gray-300" />

                            {/* Step 2 - Inactive */}
                            <div className="flex flex-col items-center">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center font-semibold mb-2">
                                    <Shield size={16} className="sm:w-5 sm:h-5" />
                                </div>
                                <span className="text-[10px] sm:text-xs font-medium text-gray-500 text-center">{t('auth:signup.steps.security')}</span>
                            </div>

                            {/* Connector 2 */}
                            <Separator className="flex-1 mx-2 sm:mx-4 mt-4 sm:mt-5 bg-gray-300" />

                            {/* Step 3 - Inactive */}
                            <div className="flex flex-col items-center">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center font-semibold mb-2">
                                    <Key size={16} className="sm:w-5 sm:h-5" />
                                </div>
                                <span className="text-[10px] sm:text-xs font-medium text-gray-500 text-center">{t('auth:signup.steps.pin')}</span>
                            </div>
                        </div>
                    </div>
                    <CardDescription className="text-center text-gray-600 pt-4">
                        {t('auth:deviceVerification.message')}
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    {/* Error Alert */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
                            <AlertCircle className="h-4 w-4" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="grid w-full items-center gap-4">
                            <div className="flex flex-col space-y-1.5">
                                <Label htmlFor="email" className="text-left">
                                    {t('auth:login.phonePlaceholder')}
                                </Label>
                                <Input
                                    id="email"
                                    type="text"
                                    placeholder={t('auth:login.emailPlaceholder')}
                                    value={identifier}
                                    onChange={(e) => {
                                        setIdentifier(e.target.value);
                                        setError(null);
                                        if (formErrors.identifier) setFormErrors({ ...formErrors, identifier: undefined });
                                    }}
                                    required
                                    disabled={isLoading}
                                    className={`pr-14 ${formErrors.identifier ? "border-red-500" : ""}`}
                                />
                                {formErrors.identifier && <p className="text-red-500 text-xs mt-1">{formErrors.identifier}</p>}
                            </div>

                            <div className="flex items-center justify-between pt-2">
                                <Button type="submit" className="w-full" disabled={isLoading}>
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {t('common:loading')}
                                        </>
                                    ) : (
                                        t('common:buttons.send')
                                    )}
                                </Button>
                            </div>
                        </div>
                    </form>
                </CardContent>
                <Separator />
                <CardFooter className="flex flex-col gap-3 pt-4">
                    <div className="text-center text-sm">
                        <span className="text-gray-600">{t('auth:signup.buttons.alreadyRegistered')}? </span>
                        <a
                            href="/login"
                            className="text-primary-600 hover:text-primary-700 hover:underline font-semibold"
                        >
                            {t('auth:login.loginButton')}
                        </a>
                    </div>
                </CardFooter>
            </Card>
        </div>
    );
}

